import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { expertiseData } from './servicesData';
import ExpertiseCard from './ExpertiseCard';
import ExpertiseNavigation from './ExpertiseNavigation';
import ExpertiseImage from '../../../assets/images/expertise-image.jpg';
import ExpertiseImage2 from '../../../assets/images/expertise-image2.png';
import StarredImage from './StarredImage';
import useIsMobile from './useIsMobile';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ExpertiseSection: React.FC = () => {
  const isMobile = useIsMobile(992);
  const allCards = expertiseData.flat();
  const totalCards = allCards.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerSlide = isMobile ? 1 : 2;
  const maxIndex = totalCards - cardsPerSlide;

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const handleNext = () => {
    if (currentIndex + cardsPerSlide <= maxIndex) {
      setCurrentIndex((prev) => prev + cardsPerSlide);
    }
  };

  const handlePrev = () => {
    if (currentIndex - cardsPerSlide >= 0) {
      setCurrentIndex((prev) => prev - cardsPerSlide);
    }
  };

  const visibleCards = allCards.slice(currentIndex, currentIndex + cardsPerSlide);

  useEffect(() => {
    if (isMobile) return;

    const slidesCount = Math.ceil(totalCards / cardsPerSlide);
    const duration = slidesCount;

    let ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top+=100',
        end: `+=${window.innerHeight * duration}`,
        pin: true,
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress;
          const newIndex = Math.floor(progress * slidesCount);
          const clampedIndex = Math.min(newIndex, maxIndex / cardsPerSlide) * cardsPerSlide;
          setCurrentIndex(clampedIndex);
        },
      });
    }, scrollRef);

    return () => ctx.revert();
  }, [cardsPerSlide, maxIndex, isMobile]);

  return (
    <section className="section-gap-lg expertise-wrapper" ref={scrollRef}>
      <div className="expertise-section" ref={sectionRef}>
        <Container>
          <Row className="justify-content-between align-items-center">
            <Col lg="5" className="expertise-left p-0">
              <div className="expertise-left-inner" data-aos="fade-right">
                <div className="expertise-header text-md-center text-lg-start text-center">
                  <h2 className="expertise-title">
                    <span>Special</span>
                    <br />
                    Expertise Areas
                  </h2>
                  <p className="expertise-description">
                    Empowering legal outcomes through expert insight, intelligent automation, and
                    strategic support across practice areas.
                  </p>
                </div>
                <div className="expertise-image-wrapper">
                  <StarredImage
                    imageSrc={
                      isMobile
                        ? currentIndex < totalCards - 3
                          ? ExpertiseImage
                          : ExpertiseImage2
                        : currentIndex === 0
                        ? ExpertiseImage
                        : ExpertiseImage2
                    }
                    starPosition="left"
                  />
                </div>
              </div>
            </Col>
            <Col lg="6" className="expertise-right p-0">
              <div className="expertise-card-group" data-aos="fade-left">
                {visibleCards.map((card) => (
                  <ExpertiseCard
                    key={card.id}
                    title={card.title}
                    description={card.description}
                    noBorder={card.id === 2 || card.id === 4}
                  />
                ))}
              </div>
              {isMobile && (
                <div className="expertise-navigation-wrapper mt-4">
                  <ExpertiseNavigation
                    onPrev={handlePrev}
                    onNext={handleNext}
                    isFirst={currentIndex === 0}
                    isLast={currentIndex >= maxIndex}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </section>
  );
};

export default ExpertiseSection;
